import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  BookOpen, 
  Users, 
  Calendar, 
  Settings, 
  LogOut, 
  Bell,
  Search,
  Menu,
  GraduationCap,
  Award,
  Library,
  NotebookPen,
  Home,
  ClipboardCheck,
  Wifi,
  WifiOff,
  RefreshCw,
  CheckSquare,
  FileCheck,
  FileText,
  KeyRound,
  ChevronDown,
  Send,
  MessageSquare,
  FlaskConical,
  Beaker,
  BarChart3,
  Clock,
  TrendingUp,
  Atom,
  Languages,
  Calculator,
  Leaf,
  Dna,
  DollarSign,
  BookMarked,
  Microscope,
  Globe,
  Scissors,
  Sparkles,
  Layers,
  ClipboardList,
  FolderOpen,
  HelpCircle,
  ShieldAlert
} from 'lucide-react';
import { CalendarDays, LayoutGrid } from 'lucide-react';
import { MissedRollCallBanner } from '@/components/attendance/MissedRollCallBanner';
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Outlet } from 'react-router-dom';
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useLanguage } from '@/contexts/LanguageContext';
import { useSync } from "../../contexts/SyncContext";
import { WhatsNewGuide, useWhatsNew } from '@/components/WhatsNewGuide';
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { getCurrentUser } from '@/data/users';
import ClassSelector from '@/components/ClassSelector';
import NotificationBell from '@/components/notifications/NotificationBell';

const SCHOOL_LOGOS = {
  'school-quatui-primary': '/quatui-logo.png',
  'school-ambaebulu-primary': '/ambaebulu-logo.png',
  'school-quirutaro-primary': '/loquirutaro-logo.png',
};

const Sidebar = ({ mobile = false, onClose }) => {
  const navigate = useNavigate();
  const [schoolName, setSchoolName] = React.useState('Penama Education Portal');
  const [schoolType, setSchoolType] = React.useState(null);
  const [teacherSubjects, setTeacherSubjects] = React.useState([]);
  const [pendingSubmissions, setPendingSubmissions] = React.useState(0);
  const { isFrench, t } = useLanguage();
  
  const currentUser = getCurrentUser();
  const isPrincipal = currentUser?.role === 'principal' || currentUser?.role === 'admin';
  const isDeputyPrincipal = currentUser?.role === 'deputy_principal';
  const isAcademicPrincipal = currentUser?.role === 'academic_principal';
  const isCoordinator = currentUser?.role === 'coordinator' || 
                        currentUser?.department?.toLowerCase()?.includes('coordinator');
  const isPrimaryCoordinator = isCoordinator && 
                               (currentUser?.department?.toLowerCase()?.includes('primary') ||
                                currentUser?.department?.toLowerCase()?.includes('primary education'));
  const canViewTeacherAttendance = isPrincipal || isCoordinator || isDeputyPrincipal || isAcademicPrincipal;
  
  // Fetch school name and type based on user's school_id (with sessionStorage cache)
  React.useEffect(() => {
    const fetchSchoolInfo = async () => {
      if (currentUser?.school_id) {
        // Check sessionStorage cache first
        const cacheKey = `school_${currentUser.school_id}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          try {
            const data = JSON.parse(cached);
            setSchoolName(data.name || 'Penama Education Portal');
            setSchoolType(data.school_type || null);
            return;
          } catch {}
        }
        try {
          const API_URL = '';
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${API_URL}/api/schools/${currentUser.school_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok && !response.bodyUsed) {
            const data = await response.json();
            setSchoolName(data.name || 'Penama Education Portal');
            setSchoolType(data.school_type || null);
            sessionStorage.setItem(cacheKey, JSON.stringify(data));
          }
        } catch (error) {
          console.error('Error fetching school:', error);
        }
      } else {
        // For coordinators or users without school_id
        setSchoolName('Penama Education Portal');
      }
    };
    fetchSchoolInfo();
  }, [currentUser?.school_id]);
  
  // Fetch teacher's subjects from their timetable (with sessionStorage cache)
  React.useEffect(() => {
    const fetchTeacherSubjects = async () => {
      if (currentUser?.role === 'teacher' && currentUser?.school_id) {
        // Check sessionStorage cache first
        const cacheKey = `subjects_${currentUser.id}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          try {
            setTeacherSubjects(JSON.parse(cached));
            return;
          } catch {}
        }
        try {
          const API_URL = '';
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${API_URL}/api/timetable/my-subjects`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok && !response.bodyUsed) {
            const data = await response.json();
            const subjects = data.subjects || [];
            setTeacherSubjects(subjects);
            sessionStorage.setItem(cacheKey, JSON.stringify(subjects));
          }
        } catch (error) {
          console.error('Error fetching teacher subjects:', error);
        }
      }
    };
    fetchTeacherSubjects();
  }, [currentUser?.id]);

  // Fetch pending submissions count for notification badge
  React.useEffect(() => {
    const fetchPendingCount = async () => {
      if (!currentUser?.id || currentUser?.role !== 'teacher') return;
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
        const [response, actRes] = await Promise.all([
          fetch(`/api/ai-lessons/all-submissions?teacher_id=${currentUser.id}&status=submitted`, { headers }),
          fetch('/api/activities/teacher/summary', { headers }),
        ]);
        let total = 0;
        if (response.ok) total += (await response.json()).total || 0;
        if (actRes.ok) total += (await actRes.json()).pending_total || 0;
        setPendingSubmissions(total);
      } catch {}
    };
    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 60000);
    return () => clearInterval(interval);
  }, [currentUser?.id]);
  
  // Determine curriculum path based on school type
  // First check currentUser.school_type (from login), then fallback to fetched schoolType
  const effectiveSchoolType = currentUser?.school_type || schoolType;
  const isPrimarySchool = effectiveSchoolType === 'primary';
  const isSecondarySchool = effectiveSchoolType === 'junior_secondary' || effectiveSchoolType === 'senior_secondary';
  const isPrimaryPrincipal = isPrimarySchool && isPrincipal;
  const curriculumPath = isPrimarySchool ? "/dashboard/primary-curriculum" : "/dashboard/curriculum";
  const curriculumLabel = isPrimarySchool ? "Primary Curriculum" : "Curriculum";
  
  // Determine which curriculum resources to show based on teacher's department AND timetable subjects
  const teacherDepartment = currentUser?.department?.toLowerCase() || '';
  // Combine timetable subjects with department for comprehensive subject detection
  const allSubjectsLower = [...teacherSubjects.map(s => s.toLowerCase()), teacherDepartment];
  
  // Helper function to check if teacher teaches a subject
  const teachesSubject = (keywords) => {
    return allSubjectsLower.some(subject => 
      keywords.some(keyword => subject.includes(keyword))
    );
  };
  
  // Home Economics check first (takes priority over general "technology")
  const isHomeEconomicsTeacher = teachesSubject(['home economics', 'textile', 'food', 'nutrition', 'garment']) ||
                                  (teachesSubject(['technology']) && !teachesSubject(['ict', 'computer']));
  
  // ICT Teacher should NOT include Home Economics teachers (exclude textile, food, nutrition, garment)
  const isICTTeacher = teachesSubject(['computer', 'ict']) || 
                        (teachesSubject(['technology']) && !isHomeEconomicsTeacher);
  
  const isChemistryTeacher = teachesSubject(['chemistry']);
  const isEconomicsTeacher = teachesSubject(['economics', 'commerce', 'business']);
  const isMathTeacher = teachesSubject(['math', 'calculus', 'statistics']);
  const isEnglishTeacher = teachesSubject(['english', 'language', 'literature']);
  const isPhysicsTeacher = teachesSubject(['physics']);
  // Science teacher should exclude Social Science teachers
  const isScienceTeacher = teachesSubject(['science', 'basic science']) && !teachesSubject(['social science']);
  const isFrenchTeacher = teachesSubject(['french']);
  const isGeographyTeacher = teachesSubject(['geography', 'humanities']);
  const isMathematicsTeacher = teachesSubject(['math', 'maths', 'mathematics', 'calculus', 'statistics']);
  const isAgricultureTeacher = teachesSubject(['agriculture', 'agri', 'farming']);
  const isBiologyTeacher = teachesSubject(['biology', 'bio', 'life science']);
  const isAccountingTeacher = teachesSubject(['accounting', 'accounts', 'bookkeeping']);
  const isHistoryTeacher = teachesSubject(['history']);
  const isESLTeacher = teachesSubject(['english', 'esl', 'literacy']);
  const isDevelopmentStudiesTeacher = teachesSubject(['development']) || currentUser?.restrictedTo === 'development-studies';
  const isSocialScienceTeacher = teachesSubject(['social science']);
  
  // Build secondary curriculum items based on teacher's subject AND school type
  const getSecondaryCurriculumItems = () => {
    const isJuniorSecondary = effectiveSchoolType === 'junior_secondary';
    const isSeniorSecondary = effectiveSchoolType === 'senior_secondary';
    
    // Principals, coordinators, and academic principals see curricula based on their school type
    if (isPrincipal || isCoordinator || isAcademicPrincipal) {
      const items = [];
      
      // Senior subjects (Year 11-13) - only for Senior Secondary
      if (isSeniorSecondary) {
        items.push({ icon: FlaskConical, label: "Chemistry", path: "/dashboard/chemistry" });
        items.push({ icon: TrendingUp, label: "Economics", path: "/dashboard/economics" });
        items.push({ icon: Atom, label: "Physics", path: "/dashboard/physics" });
        items.push({ icon: Dna, label: "Biology", path: "/dashboard/biology" });
        items.push({ icon: Languages, label: "French (7-10)", path: "/dashboard/french" });
        items.push({ icon: Languages, label: "Senior French (11-13)", path: "/dashboard/senior-french" });
        items.push({ icon: GraduationCap, label: "Geography", path: "/dashboard/geography" });
        items.push({ icon: Calculator, label: "Senior Maths (11-13)", path: "/dashboard/mathematics" });
        items.push({ icon: Leaf, label: "Agriculture", path: "/dashboard/agriculture" });
        items.push({ icon: DollarSign, label: "Accounting", path: "/dashboard/accounting" });
        items.push({ icon: BookMarked, label: "History", path: "/dashboard/history" });
        items.push({ icon: BookOpen, label: "English (ESL)", path: "/dashboard/esl" });
        items.push({ icon: Globe, label: "Development Studies", path: "/dashboard/development-studies" });
      }
      
      // Junior subjects (Year 7-10) - for Junior Secondary (and both schools share this)
      if (isJuniorSecondary || isSeniorSecondary) {
        items.push({ icon: Calculator, label: "Junior Maths (7-10)", path: "/dashboard/junior-mathematics" });
        items.push({ icon: Microscope, label: "Junior Science (7-10)", path: "/dashboard/junior-science" });
      }
      
      // Additional junior secondary subjects
      if (isJuniorSecondary) {
        items.push({ icon: Languages, label: "French (7-10)", path: "/dashboard/french" });
        items.push({ icon: BookOpen, label: "English (7-10)", path: "/dashboard/esl" });
        items.push({ icon: Leaf, label: "Junior Agriculture (7-10)", path: "/dashboard/agriculture" });
        items.push({ icon: Globe, label: "Social Science (7-10)", path: "/dashboard/social-science" });
      }
      
      return items;
    }
    
    const items = [];
    
    // Chemistry teachers see only Chemistry Curriculum (senior only)
    if (isChemistryTeacher && isSeniorSecondary) {
      items.push({ icon: FlaskConical, label: "Chemistry", path: "/dashboard/chemistry" });
    }
    
    // Physics teachers see only Physics Curriculum (senior only)
    if (isPhysicsTeacher && isSeniorSecondary) {
      items.push({ icon: Atom, label: "Physics", path: "/dashboard/physics" });
    }
    
    // General Science teachers see both Chemistry and Physics (senior only)
    if (isScienceTeacher && !isChemistryTeacher && !isPhysicsTeacher && isSeniorSecondary) {
      items.push({ icon: FlaskConical, label: "Chemistry", path: "/dashboard/chemistry" });
      items.push({ icon: Atom, label: "Physics", path: "/dashboard/physics" });
    }
    
    // Economics/Commerce/Business teachers see only Economics Curriculum (senior only)
    if (isEconomicsTeacher && isSeniorSecondary) {
      items.push({ icon: TrendingUp, label: "Economics", path: "/dashboard/economics" });
    }
    
    // French/Languages teachers see French Curriculum
    // French curriculum covers Years 7-10 (junior secondary) and 11-13 (senior)
    if (isFrenchTeacher) {
      if (isJuniorSecondary) {
        items.push({ icon: Languages, label: "French (7-10)", path: "/dashboard/french" });
      } else if (isSeniorSecondary) {
        items.push({ icon: Languages, label: "Senior French (11-13)", path: "/dashboard/senior-french" });
      }
    }
    
    // Science teachers see Junior Science (7-10)
    if (isScienceTeacher) {
      items.push({ icon: Microscope, label: "Junior Science (7-10)", path: "/dashboard/junior-science" });
    }
    
    // Geography/Social Science/Humanities teachers see Geography Curriculum (senior only)
    if (isGeographyTeacher && isSeniorSecondary) {
      items.push({ icon: GraduationCap, label: "Geography", path: "/dashboard/geography" });
    }
    
    // Social Science teachers see Social Science curriculum
    if (isSocialScienceTeacher) {
      if (isJuniorSecondary) {
        items.push({ icon: Globe, label: "Social Science (7-10)", path: "/dashboard/social-science" });
      }
      if (isSeniorSecondary) {
        items.push({ icon: Globe, label: "Social Science", path: "/dashboard/social-science" });
      }
    }
    
    // Mathematics teachers see appropriate curriculum based on school type and restrictions
    if (isMathematicsTeacher) {
      const isSeniorMathOnly = currentUser?.restrictedTo === 'senior-mathematics';
      const isJuniorMathOnly = currentUser?.restrictedTo === 'junior-mathematics';
      
      // Show Senior Math for senior secondary teachers (unless restricted to junior only)
      if (isSeniorSecondary && !isJuniorMathOnly) {
        items.push({ icon: Calculator, label: "Senior Maths (11-13)", path: "/dashboard/mathematics" });
      }
      // Show Junior Math unless restricted to senior only
      if (!isSeniorMathOnly) {
        items.push({ icon: Calculator, label: "Junior Maths (7-10)", path: "/dashboard/junior-mathematics" });
      }
    }
    
    // Agriculture teachers see Agriculture Curriculum
    // For junior secondary, show Junior Agriculture (using same curriculum for now)
    if (isAgricultureTeacher) {
      if (isJuniorSecondary) {
        items.push({ icon: Leaf, label: "Junior Agriculture (7-10)", path: "/dashboard/agriculture" });
      }
      if (isSeniorSecondary) {
        items.push({ icon: Leaf, label: "Agriculture", path: "/dashboard/agriculture" });
      }
    }
    
    // Biology teachers see Biology Curriculum (senior only)
    if (isBiologyTeacher && isSeniorSecondary) {
      items.push({ icon: Dna, label: "Biology", path: "/dashboard/biology" });
    }
    
    // Accounting teachers see Accounting Curriculum (senior only)
    if (isAccountingTeacher && isSeniorSecondary) {
      items.push({ icon: DollarSign, label: "Accounting", path: "/dashboard/accounting" });
    }
    
    // History teachers see History Curriculum (senior only)
    if (isHistoryTeacher && isSeniorSecondary) {
      items.push({ icon: BookMarked, label: "History", path: "/dashboard/history" });
    }
    
    // ESL teachers see English Curriculum
    // Junior English uses ESL curriculum page for now
    if (isESLTeacher) {
      if (isJuniorSecondary) {
        items.push({ icon: BookOpen, label: "English (7-10)", path: "/dashboard/esl" });
      }
      if (isSeniorSecondary) {
        items.push({ icon: BookOpen, label: "English (ESL)", path: "/dashboard/esl" });
      }
    }
    
    // Development Studies teachers see Development Studies Curriculum (senior only)
    if (isDevelopmentStudiesTeacher && isSeniorSecondary) {
      items.push({ icon: Globe, label: "Development Studies", path: "/dashboard/development-studies" });
    }
    
    // Home Economics / Technology teachers see Home Economics Curriculum
    if (isHomeEconomicsTeacher) {
      items.push({ icon: Scissors, label: "Home Economics", path: "/dashboard/home-economics" });
    }
    
    // If teacher doesn't have a specific subject assigned, show based on school type (ICT teachers use the Resource Library)
    if (items.length === 0 && !isICTTeacher) {
      if (isSeniorSecondary) {
        items.push({ icon: FlaskConical, label: "Chemistry", path: "/dashboard/chemistry" });
        items.push({ icon: TrendingUp, label: "Economics", path: "/dashboard/economics" });
        items.push({ icon: Atom, label: "Physics", path: "/dashboard/physics" });
        items.push({ icon: Dna, label: "Biology", path: "/dashboard/biology" });
        items.push({ icon: Languages, label: "French", path: "/dashboard/french" });
        items.push({ icon: GraduationCap, label: "Geography", path: "/dashboard/geography" });
        items.push({ icon: Calculator, label: "Senior Maths (11-13)", path: "/dashboard/mathematics" });
        items.push({ icon: Leaf, label: "Agriculture", path: "/dashboard/agriculture" });
        items.push({ icon: DollarSign, label: "Accounting", path: "/dashboard/accounting" });
        items.push({ icon: BookMarked, label: "History", path: "/dashboard/history" });
        items.push({ icon: BookOpen, label: "English (ESL)", path: "/dashboard/esl" });
        items.push({ icon: Globe, label: "Development Studies", path: "/dashboard/development-studies" });
      }
      items.push({ icon: Calculator, label: "Junior Maths (7-10)", path: "/dashboard/junior-mathematics" });
    }
    
    return items;
  };
  
  // Helper function to get menu label based on language
  const getLabel = (englishLabel, frenchKey) => {
    if (isFrench && frenchKey) {
      return t(`sidebar.${frenchKey}`, englishLabel);
    }
    return englishLabel;
  };
  
  const menuGroups = React.useMemo(() => [
    {
      title: getLabel("Dashboard", "dashboard"),
      color: "#92EAD9",
      items: [
        { icon: Home, label: getLabel("Home", "home"), path: "/dashboard" },
      ]
    },
    // Teaching section - show for teachers, principals, and academic principals
    ...(!isDeputyPrincipal ? [{
      title: getLabel("Teaching", "teaching"),
      color: "#90A2EC",
      items: [
        { icon: BookOpen, label: getLabel("Classes", "classes"), path: "/dashboard/classes" },
        ...(!isPrimaryPrincipal ? [{ icon: CheckSquare, label: getLabel("Roll Call", "rollCall"), path: "/dashboard/roll-call" }] : []),
        { icon: Users, label: getLabel("Students", "students"), path: "/dashboard/students" },
        ...((isPrincipal || isAcademicPrincipal || isDeputyPrincipal) ? [
          { icon: GraduationCap, label: "Assign Students to Subjects", path: "/dashboard/student-enrollment" },
        ] : []),
        // My Attendance - show for all teaching staff including principal, academic principal, and deputy principal
        ...(!isPrimaryPrincipal ? [{ icon: ClipboardCheck, label: getLabel("My Attendance", "myAttendance"), path: "/dashboard/my-attendance" }] : []),
      ]
    }] : []),
    // Assessment & Grading - show for teachers and academic principals
    ...(!isDeputyPrincipal ? [{
      title: getLabel("Assessment & Grading", "assessmentGrading"),
      color: "#90ECBA",
      items: [
        ...(isSecondarySchool ? [
          { icon: ClipboardList, label: "IA Program", path: "/dashboard/ia-program" },
          { icon: Calendar, label: "IA Calendar", path: "/dashboard/ia-calendar" },
        ] : []),
        { icon: GraduationCap, label: getLabel("Grade Book", "gradeBook"), path: "/dashboard/gradebook" },
        // Student Submissions - for all teachers who share assessments
        ...(!isPrimaryPrincipal ? [
          { icon: MessageSquare, label: getLabel("Student Work", "studentWork"), path: "/dashboard/student-submissions", badge: pendingSubmissions },
          ...(!isPrimarySchool ? [{ icon: ClipboardCheck, label: "Quick Check Practice", path: "/dashboard/quick-check-progress" }] : []),
          { icon: ClipboardList, label: "Activities & Tests", path: "/dashboard/activities" },
        ] : []),
        { icon: FileText, label: getLabel("View Report Cards", "viewReportCards"), path: "/dashboard/view-report-cards" },
        { icon: BarChart3, label: "Performance Analysis", path: "/dashboard/performance" },
      ]
    }] : []),
    // Resources section
    ...(!isDeputyPrincipal ? [{
      title: getLabel("Resources", "resources"),
      color: "#A7F389",
      items: [
        // Primary Coordinators should see Primary Curriculum
        ...(isPrimaryCoordinator ? [
          { icon: Library, label: getLabel("Primary Curriculum", "primaryCurriculum"), path: "/dashboard/primary-curriculum" },
        ] : isPrimarySchool && !isPrimaryPrincipal ? [
          { icon: Library, label: isFrench ? t('sidebar.primaryCurriculum', curriculumLabel) : curriculumLabel, path: curriculumPath },
        ] : []),
        ...(!isPrimarySchool ? [{ icon: Calendar, label: getLabel("Timetable", "timetable"), path: "/dashboard/timetable" }] : []),
        ...(isPrimarySchool && !isFrench ? [{ icon: BookOpen, label: "Literacy Program (Y1–3)", path: "/dashboard/literacy-program" }] : []),
        ...(!isPrimarySchool ? [{ icon: Library, label: "Resource Library", path: "/dashboard/resource-library" }] : []),
        ...(isPrimarySchool || currentUser?.email?.toLowerCase() === 'roytari@spc.edu.vu' ? [{ icon: Library, label: "Primary Resource Library", path: "/dashboard/primary-resource-library" }] : []),
        ...(['roytari@spc.edu.vu', 'watsonarnabat@spc.edu.vu'].includes(currentUser?.email?.toLowerCase()) ? [
          { icon: ClipboardCheck, label: "AI Exam Marking", path: "/dashboard/exam-marking" },
        ] : []),
        ...(currentUser?.email?.toLowerCase() === 'roytari@spc.edu.vu' ? [
          { icon: Sparkles, label: "AI Course Generator", path: "/dashboard/course-generator" },
          { icon: GraduationCap, label: "Assign Students to Subjects", path: "/dashboard/student-enrollment" },
          { icon: Award, label: "Certificates", path: "/dashboard/certificates" },
          { icon: ShieldAlert, label: "Login Health Check", path: "/dashboard/login-health" },
          { icon: Calendar, label: "Master Timetable", path: "/dashboard/master-timetable" },
          { icon: CalendarDays, label: "Term Dates", path: "/dashboard/term-dates" },
          { icon: LayoutGrid, label: "Attendance Overview", path: "/dashboard/attendance-overview" },
        ] : []),
        ...(['admin', 'coordinator', 'principal', 'super_admin'].includes(currentUser?.role) ? [
          { icon: MessageSquare, label: "SMS to Parents", path: "/dashboard/sms-notifications" },
        ] : []),
      ]
    }] : []),
    // Teacher Planning section - PRIMARY SCHOOL TEACHERS ONLY
    ...(isPrimarySchool && currentUser?.role === 'teacher' ? [{
      title: "Teacher Planning",
      color: "#C4B5FD",
      items: [
        { icon: BookOpen, label: "Story Generator", path: "/dashboard/story-generator" },
      ]
    }] : []),
    // Deputy Principal section - Discipline & Staff Management
    ...(isDeputyPrincipal ? [{
      title: "My Work",
      color: "#A7F3D0",
      items: [
        { icon: ClipboardCheck, label: getLabel("My Attendance", "myAttendance"), path: "/dashboard/my-attendance" },
        { icon: BookOpen, label: getLabel("Classes", "classes"), path: "/dashboard/classes" },
        { icon: CheckSquare, label: getLabel("Roll Call", "rollCall"), path: "/dashboard/roll-call" },
      ]
    }, {
      title: "Discipline Management",
      color: "#FCA5A5",
      items: [
        { icon: Users, label: getLabel("Students", "students"), path: "/dashboard/students" },
        { icon: ClipboardCheck, label: "Discipline Records", path: "/dashboard/discipline" },
        { icon: FileText, label: "Incident Reports", path: "/dashboard/incidents" },
      ]
    }, {
      title: "Staff Management",
      color: "#FDBA74",
      items: [
        { icon: Users, label: getLabel("Teacher Attendance", "teacherAttendance"), path: "/dashboard/teacher-attendance" },
        { icon: Clock, label: getLabel("Leave Requests", "leaveRequests"), path: "/dashboard/leave-requests" },
        { icon: Calendar, label: getLabel("Timetable", "timetable"), path: "/dashboard/timetable" },
        { icon: LayoutGrid, label: "Attendance Overview", path: "/dashboard/attendance-overview" },
        ...(!isPrimarySchool && !isDeputyPrincipal ? [{ icon: Calendar, label: "Master Timetable", path: "/dashboard/master-timetable" }] : []),
        { icon: Bell, label: "Staff Notices", path: "/dashboard/send-reminders" },
      ]
    }] : []),
    // Academic Principal section - Curriculum & Syllabus Monitoring
    ...(isAcademicPrincipal ? [{
      title: "My Work",
      color: "#FCA5A5",
      items: [
        { icon: ClipboardCheck, label: getLabel("My Attendance", "myAttendance"), path: "/dashboard/my-attendance" },
        { icon: BookOpen, label: getLabel("Classes", "classes"), path: "/dashboard/classes" },
        { icon: CheckSquare, label: getLabel("Roll Call", "rollCall"), path: "/dashboard/roll-call" },
      ]
    }, {
      title: "Curriculum Monitoring",
      color: "#93C5FD",
      items: [
        { icon: Calendar, label: "Master Timetable", path: "/dashboard/master-timetable" },
        { icon: LayoutGrid, label: "Attendance Overview", path: "/dashboard/attendance-overview" },
        { icon: BookOpen, label: "Syllabus Progress", path: "/dashboard/syllabus-progress" },
        { icon: BarChart3, label: "Teaching Progress", path: "/dashboard/teaching-progress" },
        { icon: TrendingUp, label: "Curriculum Completion", path: "/dashboard/curriculum-completion" },
        { icon: Layers, label: "Curriculum Overview", path: "/dashboard/curriculum-overview" },
        // Primary Coordinators should see Primary Curriculum
        ...(isPrimaryCoordinator ? [
          { icon: Library, label: getLabel("Primary Curriculum", "primaryCurriculum"), path: "/dashboard/primary-curriculum" },
        ] : isPrimarySchool ? [
          { icon: Library, label: curriculumLabel, path: curriculumPath },
        ] : []),
      ]
    }, {
      title: "Academic Oversight",
      color: "#A5B4FC",
      items: [
        { icon: FileCheck, label: "Teachers Planning", path: "/dashboard/pending-approvals" },
        { icon: Users, label: getLabel("Teacher Attendance", "teacherAttendance"), path: "/dashboard/teacher-attendance" },
        { icon: GraduationCap, label: "Grade Analytics", path: "/dashboard/gradebook" },
        { icon: BarChart3, label: "Academic Reports", path: "/dashboard/analytics" },
        { icon: FileText, label: getLabel("View Report Cards", "viewReportCards"), path: "/dashboard/view-report-cards" },
        { icon: Award, label: "Certificates", path: "/dashboard/certificates" },
      ]
    }] : []),
    // Coordinator section - can view teacher attendance
    ...(isCoordinator && !isPrincipal ? [{
      title: "Coordination",
      color: "#A7F3D0",
      items: [
        { icon: Users, label: getLabel("Teacher Attendance", "teacherAttendance"), path: "/dashboard/teacher-attendance" },
        { icon: Layers, label: "Curriculum Overview", path: "/dashboard/curriculum-overview" },
        { icon: BarChart3, label: "Teaching Progress", path: "/dashboard/teaching-progress" },
      ]
    }] : []),
    ...(isPrincipal ? [{
      title: getLabel("Administration", "administration"),
      color: "#EFFB81",
      items: [
        // Teacher Plans review - PRIMARY SCHOOL PRINCIPALS ONLY
        ...(isPrimarySchool ? [
          { icon: FolderOpen, label: "Teacher Plans", path: "/dashboard/pending-approvals" },
        ] : []),
        ...(!isPrimarySchool ? [
          { icon: Calendar, label: "Master Timetable", path: "/dashboard/master-timetable" },
          { icon: BarChart3, label: "Teaching Progress", path: "/dashboard/teaching-progress" },
        ] : []),
        { icon: Users, label: getLabel("Teacher Attendance", "teacherAttendance"), path: "/dashboard/teacher-attendance" },
        { icon: LayoutGrid, label: "Attendance Overview", path: "/dashboard/attendance-overview" },
        { icon: Clock, label: getLabel("Leave Requests", "leaveRequests"), path: "/dashboard/leave-requests" },
        { icon: Bell, label: getLabel("Send Reminders", "sendReminders"), path: "/dashboard/send-reminders" },
        { icon: Send, label: getLabel("Publish Reports", "publishReports"), path: "/dashboard/publish-reports" },
        { icon: FileText, label: getLabel("View Report Cards", "viewReportCards"), path: "/dashboard/view-report-cards" },
        { icon: Award, label: "Certificates", path: "/dashboard/certificates" },
        ...(isSecondarySchool ? [
          { icon: Beaker, label: "Student Streams", path: "/dashboard/student-subjects" },
          { icon: BarChart3, label: "Stream Analytics", path: "/dashboard/stream-report" }
        ] : []),
        { icon: RefreshCw, label: getLabel("Reset Term Data", "resetTermData"), path: "/dashboard/admin-reset" },
      ]
    }] : []),
  ], [isPrincipal, isDeputyPrincipal, isAcademicPrincipal, isCoordinator, curriculumLabel, curriculumPath, isPrimarySchool, isPrimaryPrincipal, isSecondarySchool, isICTTeacher, isChemistryTeacher, isPhysicsTeacher, effectiveSchoolType, getSecondaryCurriculumItems, teacherSubjects, isFrench, t]);

  const handleLogout = () => {
    // Clear all authentication data from localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('user');
    
    navigate('/');
    if (onClose) onClose();
  };

  return (
    <div className="h-full flex flex-col bg-card border-r">
      <div className="p-6 border-b flex items-center gap-2">
        <img 
          src={SCHOOL_LOGOS[currentUser?.school_id] || "https://customer-assets.emergentagent.com/job_edtech-penama/artifacts/4xob6y79_logo12_9_73521.jpeg"} 
          alt={`${schoolName} logo`} 
          className="h-10 w-10 object-contain"
          data-testid="sidebar-school-logo"
        />
        <div className="flex flex-col">
          <span className="font-bold text-base tracking-tight leading-tight">{schoolName}</span>
          <span className="text-[10px] text-gray-600 leading-tight">Penama Province</span>
        </div>
      </div>

      <div className="flex-1 py-4 px-3 space-y-3 overflow-y-auto">
        {menuGroups.map((group, groupIndex) => (
          <div 
            key={groupIndex} 
            className="rounded-lg p-2 space-y-1"
            style={{ backgroundColor: group.color }}
          >
            <div className="px-3 mb-2 text-xs font-bold text-gray-800 uppercase tracking-wider text-center">
              {group.title}
            </div>
            {group.items.map((item) => (
              <Link 
                key={item.path} 
                to={item.path}
                onClick={onClose}
              >
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 text-sm text-gray-800 hover:text-gray-900 hover:bg-white/50 font-medium"
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                  {item.badge > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1" data-testid="pending-badge">
                      {item.badge}
                    </span>
                  )}
                </Button>
              </Link>
            ))}
          </div>
        ))}
        
        <div 
          className="rounded-lg p-2 space-y-1 mt-2"
          style={{ backgroundColor: "#9AFD7F" }}
        >
          <div className="px-3 mb-2 text-xs font-bold text-gray-800 uppercase tracking-wider text-center">
            {getLabel("Account", "account")}
          </div>
          <Link 
            to="/dashboard/change-password"
            onClick={onClose}
          >
            <Button variant="ghost" className="w-full justify-start gap-3 text-sm text-gray-800 hover:text-gray-900 hover:bg-white/50 font-medium">
              <KeyRound className="h-4 w-4" />
              {getLabel("Change Password", "changePassword")}
            </Button>
          </Link>
        </div>
      </div>

      <div className="p-4 border-t bg-muted/20 space-y-1">
        <Button 
          variant="ghost" 
          className="w-full justify-start gap-3 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
          onClick={() => {
            window.dispatchEvent(new CustomEvent('open-whats-new'));
            if (mobile && onClose) onClose();
          }}
          data-testid="help-guide-btn"
        >
          <HelpCircle className="h-4 w-4" />
          {getLabel("Help Guide", "helpGuide")}
        </Button>
        <Button 
          variant="ghost" 
          className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4" />
          {getLabel("Sign Out", "signOut")}
        </Button>
      </div>
    </div>
  );
};

export default function DashboardLayout() {
  const navigate = useNavigate();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const { isOffline, toggleOfflineMode, pendingChanges, syncNow, isSyncing } = useSync();
  const currentUser = getCurrentUser();
  const isPrincipal = currentUser?.role === 'principal';
  const isTeacher = currentUser?.role === 'teacher';
  const { shouldShow: shouldShowWhatsNew, markSeen } = useWhatsNew();

  // Show What's New on first login
  React.useEffect(() => {
    if (shouldShowWhatsNew) {
      const timer = setTimeout(() => setShowGuide(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [shouldShowWhatsNew]);

  // Listen for manual open from sidebar button
  React.useEffect(() => {
    const handler = () => setShowGuide(true);
    window.addEventListener('open-whats-new', handler);
    return () => window.removeEventListener('open-whats-new', handler);
  }, []);

  const handleClassChange = (classData) => {
    setSelectedClass(classData);
    // Store in localStorage for persistence
    localStorage.setItem('selectedClass', JSON.stringify(classData));
    // Dispatch custom event for same-tab listeners (storage event only fires for other tabs)
    window.dispatchEvent(new CustomEvent('classSelectionChanged', { detail: classData }));
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <div className="hidden md:block w-64 fixed inset-y-0 z-30">
        <Sidebar />
      </div>

      {/* Main Content */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen transition-all duration-300 ease-in-out">
        {/* Top Navigation */}
        <header className="sticky top-0 z-20 w-full h-16 bg-background/80 backdrop-blur-md border-b px-4 md:px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Mobile Menu */}
            <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64 border-r">
                <Sidebar mobile onClose={() => setIsMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            
            {/* Class Selector for Teachers */}
            {isTeacher && (
              <div className="hidden sm:block w-64 lg:w-96">
                <ClassSelector 
                  onClassChange={handleClassChange}
                  currentClass={selectedClass?.id}
                />
              </div>
            )}
            
            {!isTeacher && (
              <div className="relative hidden sm:block w-64 lg:w-96">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search classes, students, assignments..." 
                  className="pl-9 bg-muted/40 border-muted focus-visible:ring-primary/20"
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 md:gap-6">
            {/* Sync / Offline Controls */}
            <div className="flex items-center gap-2 mr-4">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className={`gap-2 ${isOffline ? 'text-orange-500' : 'text-green-600'}`}
                      onClick={toggleOfflineMode}
                    >
                      {isOffline ? <WifiOff className="h-4 w-4" /> : <Wifi className="h-4 w-4" />}
                      <span className="hidden md:inline text-xs font-medium">
                        {isOffline ? 'Offline Mode' : 'Online'}
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Click to toggle {isOffline ? 'Online' : 'Offline'} mode</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <Button 
                variant={pendingChanges.length > 0 ? "default" : "outline"}
                size="sm"
                className="gap-2 relative"
                onClick={syncNow}
                disabled={isSyncing || (pendingChanges.length === 0)}
              >
                <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                <span className="hidden md:inline">Sync</span>
                {pendingChanges.length > 0 && (
                  <Badge variant="destructive" className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-[10px]">
                    {pendingChanges.length}
                  </Badge>
                )}
              </Button>
            </div>

            {/* Notification Bell - shows for teachers with unread count */}
            <NotificationBell />
            
            <div className="flex items-center gap-3 border-l pl-6">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-3 hover:bg-muted/50 h-auto p-2 pr-3">
                    <div className="text-right hidden md:block">
                      <p className="text-sm font-medium">{currentUser?.name || 'User'}</p>
                      <p className="text-xs text-muted-foreground">
                        {currentUser?.role === 'admin' && 'Administrator'}
                        {currentUser?.role === 'principal' && 'Principal'}
                        {currentUser?.role === 'deputy_principal' && 'Deputy Principal'}
                        {currentUser?.role === 'academic_principal' && 'Academic Principal'}
                        {currentUser?.role === 'teacher' && (currentUser?.department || 'Teacher')}
                        {currentUser?.role === 'coordinator' && 'Coordinator'}
                      </p>
                    </div>
                    <Avatar className="h-9 w-9 border-2 border-primary/10">
                      <AvatarImage src="https://github.com/shadcn.png" alt={currentUser?.name} />
                      <AvatarFallback>
                        {currentUser?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard/change-password" className="flex items-center gap-2 cursor-pointer">
                      <KeyRound className="h-4 w-4" />
                      Change Password
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <button className="flex items-center gap-2 w-full cursor-pointer">
                      <Settings className="h-4 w-4" />
                      Settings
                    </button>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => {
                      localStorage.removeItem('authToken');
                      localStorage.removeItem('token');
                      localStorage.removeItem('currentUser');
                      localStorage.removeItem('user');
                      navigate('/');
                    }}
                    className="text-destructive focus:text-destructive cursor-pointer"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto w-full animate-in fade-in-50 slide-in-from-bottom-2 duration-500">
            <MissedRollCallBanner />
            <Outlet />
          </div>
        </main>
      </div>
      <WhatsNewGuide
        isOpen={showGuide}
        onClose={() => {
          setShowGuide(false);
          markSeen();
        }}
      />
    </div>
  );
}
